import React, { useState } from 'react';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import { AN_DATA, ENG_ALL, HEAT_DATA } from '../../constants';

type TimePeriod = '7d' | '30d' | 'all';
type AnView = 'all' | 'playbooks' | 'collaterals';

const TYPE_ICONS: Record<string, string> = { pdf: 'ti-file-type-pdf', ppt: 'ti-presentation', video: 'ti-player-play', url: 'ti-world-www' };
function hcol(v: number, max: number) {
  const p = v / max;
  if (p < .15) return '#E2E8F0'; if (p < .35) return '#FDF4E7'; if (p < .6) return '#F6C87A'; if (p < .8) return '#C8973A'; return '#8A6020';
}

export default function AnalyticsScreen() {
  const { isMobile, rp } = useBreakpoint();
  const [time, setTime] = useState<TimePeriod>('7d');
  const [anView, setAnView] = useState<AnView>('all');
  const data = AN_DATA[time];
  const maxBar = Math.max(...data.bars.map(b => b.v));
  const px = rp({ mobile: '14px', tablet: '20px', desktop: '24px' });

  return (
    <div style={{ flex: 1, overflowY: 'auto' }}>
      <div style={{ padding: `16px ${px}`, maxWidth: '1100px', margin: '0 auto' }}>
        {/* Top bar */}
        <div style={{ display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', justifyContent: 'space-between', marginBottom: '16px', flexDirection: isMobile ? 'column' : 'row', gap: '10px' }}>
          <div>
            <div style={{ fontSize: rp({ mobile: '18px', desktop: '20px' }), fontWeight: '800', color: 'var(--navy)' }}>Analytics</div>
            <div style={{ fontSize: '12px', color: 'var(--text3)' }}>Engagement & reach</div>
          </div>
          <div style={{ display: 'flex', gap: '4px', background: 'var(--surface)', borderRadius: '8px', padding: '3px' }}>
            {(['7d', '30d', 'all'] as TimePeriod[]).map(t => (
              <button key={t} onClick={() => setTime(t)} style={{ padding: '6px 12px', borderRadius: '6px', border: 'none', fontFamily: 'inherit', fontSize: '12px', fontWeight: '600', cursor: 'pointer', background: time === t ? 'white' : 'none', color: time === t ? 'var(--navy)' : 'var(--text3)', boxShadow: time === t ? '0 1px 4px rgba(0,0,0,.1)' : 'none' }}>
                {t === '7d' ? '7d' : t === '30d' ? '30d' : 'All'}
              </button>
            ))}
          </div>
        </div>

        {/* KPI Cards — 4 cols → 2x2 */}
        <div style={{ display: 'grid', gridTemplateColumns: rp({ mobile: '1fr 1fr', desktop: 'repeat(4,1fr)' }), gap: '10px', marginBottom: '16px' }}>
          {[
            { icon: 'ti-eye',    label: 'Page Views',      val: data.kpi.views.toLocaleString(),    color: '#1E4E8C', bg: '#EBF8FF', change: '+24%' },
            { icon: 'ti-users',  label: 'Visitors',        val: data.kpi.visitors.toLocaleString(), color: '#276749', bg: '#F0FFF4', change: '+11%' },
            { icon: 'ti-share',  label: 'Links Shared',    val: data.kpi.links.toString(),          color: '#7A5A1A', bg: '#FDF4E7', change: '+7'   },
            { icon: 'ti-target', label: 'Conversions',     val: data.kpi.conv.toString(),           color: '#C53030', bg: '#FFF5F5', change: '+5'   },
          ].map(kpi => (
            <div key={kpi.label} style={{ background: 'white', border: '1.5px solid var(--border)', borderRadius: '12px', padding: '12px 14px', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
              <div style={{ width: '34px', height: '34px', borderRadius: '9px', background: kpi.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <i className={`ti ${kpi.icon}`} style={{ color: kpi.color, fontSize: '16px' }} />
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: '10px', color: 'var(--text3)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: '3px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{kpi.label}</div>
                <div style={{ fontSize: rp({ mobile: '20px', desktop: '24px' }), fontWeight: '800', color: 'var(--navy)', lineHeight: 1 }}>{kpi.val}</div>
                <div style={{ fontSize: '10px', color: '#22C55E', marginTop: '2px', fontWeight: '600' }}>{kpi.change}</div>
              </div>
            </div>
          ))}
        </div>

        {/* View switcher */}
        <div style={{ display: 'flex', gap: '4px', marginBottom: '14px', background: 'var(--surface)', borderRadius: '8px', padding: '3px', width: 'fit-content' }}>
          {(['all', 'playbooks', 'collaterals'] as AnView[]).map(v => (
            <button key={v} onClick={() => setAnView(v)} style={{ padding: '6px 12px', borderRadius: '6px', border: 'none', fontFamily: 'inherit', fontSize: '12px', fontWeight: '600', cursor: 'pointer', background: anView === v ? 'white' : 'none', color: anView === v ? 'var(--navy)' : 'var(--text3)', boxShadow: anView === v ? '0 1px 4px rgba(0,0,0,.1)' : 'none', textTransform: 'capitalize', whiteSpace: 'nowrap' }}>
              {v === 'all' ? 'All' : v.charAt(0).toUpperCase() + v.slice(1)}
            </button>
          ))}
        </div>

        {/* Charts — 2 cols → stacked */}
        <div style={{ display: 'grid', gridTemplateColumns: rp({ mobile: '1fr', desktop: '1fr 1fr' }), gap: '12px', marginBottom: '12px' }}>
          {/* Bar chart */}
          <div style={{ background: 'white', border: '1.5px solid var(--border)', borderRadius: '12px', padding: '14px' }}>
            <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--navy)', marginBottom: '14px' }}>Views</div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: rp({ mobile: '3px', desktop: '6px' }), height: '110px' }}>
              {data.bars.map((b, i) => (
                <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px', height: '100%', justifyContent: 'flex-end' }}>
                  {!isMobile && <div style={{ fontSize: '8px', color: 'var(--text3)', fontWeight: '600', whiteSpace: 'nowrap' }}>{b.v > 999 ? (b.v/1000).toFixed(1)+'k' : b.v}</div>}
                  <div style={{ width: '100%', background: '#C8973A', borderRadius: '3px 3px 0 0', height: `${(b.v / maxBar) * 100}%`, transition: 'height .4s', cursor: 'pointer', minHeight: '2px' }}
                    onMouseEnter={e => (e.currentTarget.style.background = '#E8B55A')}
                    onMouseLeave={e => (e.currentTarget.style.background = '#C8973A')} />
                  <div style={{ fontSize: '8px', color: 'var(--text3)', fontWeight: '500', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%', textAlign: 'center' }}>{b.d}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Top engagement */}
          <div style={{ background: 'white', border: '1.5px solid var(--border)', borderRadius: '12px', padding: '14px' }}>
            <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--navy)', marginBottom: '12px' }}>Top by Engagement</div>
            {ENG_ALL.map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                <i className={`ti ${TYPE_ICONS[item.type] || 'ti-file'}`} style={{ fontSize: '13px', color: 'var(--text3)', flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '11px', fontWeight: '600', color: 'var(--navy)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</div>
                  <div style={{ height: '4px', background: 'var(--border)', borderRadius: '2px', marginTop: '3px' }}>
                    <div style={{ width: `${item.pct}%`, height: '100%', background: '#C8973A', borderRadius: '2px' }} />
                  </div>
                </div>
                <div style={{ flexShrink: 0, textAlign: 'right' }}>
                  <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--navy)' }}>{item.pct}%</div>
                  {!isMobile && <div style={{ fontSize: '9px', color: 'var(--text3)' }}>{item.views}v</div>}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Table — scrollable on mobile */}
        <div style={{ background: 'white', border: '1.5px solid var(--border)', borderRadius: '12px', padding: '14px', marginBottom: '12px', overflowX: 'auto' }}>
          <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--navy)', marginBottom: '10px' }}>All Content</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', minWidth: isMobile ? '460px' : undefined }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                {['Name', 'Type', 'Views', 'Engagement', ...(isMobile ? [] : ['Avg Time'])].map(h => (
                  <th key={h} style={{ padding: '7px 8px', textAlign: 'left', fontSize: '10px', fontWeight: '700', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.05em', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ENG_ALL.map((item, i) => (
                <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '9px 8px', fontWeight: '600', color: 'var(--navy)', maxWidth: isMobile ? '100px' : '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</td>
                  <td style={{ padding: '9px 8px', whiteSpace: 'nowrap' }}>
                    <span style={{ fontSize: '10px', fontWeight: '600', padding: '2px 7px', borderRadius: '4px', background: 'var(--tag-bg)', color: 'var(--text2)', textTransform: 'uppercase' }}>{item.type}</span>
                  </td>
                  <td style={{ padding: '9px 8px', fontWeight: '700', color: 'var(--navy)', whiteSpace: 'nowrap' }}>{item.views.toLocaleString()}</td>
                  <td style={{ padding: '9px 8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <div style={{ width: '50px', height: '5px', background: 'var(--border)', borderRadius: '3px', flexShrink: 0 }}>
                        <div style={{ width: `${item.pct}%`, height: '100%', background: '#C8973A', borderRadius: '3px' }} />
                      </div>
                      <span style={{ fontWeight: '600', color: 'var(--navy)', whiteSpace: 'nowrap' }}>{item.pct}%</span>
                    </div>
                  </td>
                  {!isMobile && <td style={{ padding: '9px 8px', color: 'var(--text3)' }}>{'3:'+(i*22+10).toString().padStart(2,'0')}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Page heatmap */}
        <div style={{ background: 'white', border: '1.5px solid var(--border)', borderRadius: '12px', padding: '14px' }}>
          <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--navy)', marginBottom: '12px' }}>
            Page Heatmap <span style={{ fontSize: '10px', fontWeight: '400', color: 'var(--text3)' }}>avg seconds per page</span>
          </div>
          {HEAT_DATA.map((item, i) => {
            const mx = Math.max(...item.pages);
            return (
              <div key={i} style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text2)', marginBottom: '5px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</div>
                <div style={{ display: 'flex', gap: '4px' }}>
                  {item.pages.map((sec, j) => (
                    <div key={j} title={`Page ${j+1}: ${sec}s`} style={{ flex: 1, height: '28px', borderRadius: '4px', background: hcol(sec, mx), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', fontWeight: '700', color: sec/mx > 0.6 ? 'white' : 'var(--text2)' }}>
                      {sec}s
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
